import React from "react";
import styled, { keyframes } from "styled-components";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaClock, FaUsers, FaStar } from "react-icons/fa";
import { ArrowUpRight } from "lucide-react";
import pintura from "./Images/pintando2.jpeg";
import vino from "./Images/ceramica.png";
import vino1 from "./Images/vino.png";

const rise = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Wrapper = styled.section`
  padding: 7rem 1rem 5rem;
  background: #f3ecdf;

  @media (min-width: 900px) {
    padding: 10rem 4vw 8rem;
  }
`;

const Container = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`;

const Hero = styled.div`
  text-align: left;
  margin-bottom: clamp(4rem, 9vw, 9rem);
  animation: ${rise} 0.55s ease both;
`;

const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: Arial, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: #6f6256;
`;

const Title = styled.h1`
  margin-top: 0.55rem;
  color: #204a3f;
  max-width: 9ch;
  font-size: clamp(3.8rem, 11vw, 9rem);
  font-weight: 450;
  line-height: .82;
  letter-spacing: -.065em;
  text-wrap: balance;
`;

const Subtitle = styled.p`
  margin: 1.5rem 0 0 auto;
  color: #5f5750;
  max-width: 42ch;
  font-family: Arial, sans-serif;
  font-size: clamp(1rem, 3.8vw, 1.25rem);
  line-height: 1.6;
`;

const Perks = styled.div`
  margin: 1.5rem 0 0 auto;
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
  justify-content: flex-start;
  max-width: 42rem;
`;

const Perk = styled.span`
  border: 0;
  border-left: 1px solid rgba(45, 91, 79, 0.3);
  background: transparent;
  color: #2d5b4f;
  font-size: 0.83rem;
  border-radius: 0;
  padding: 0 0 0 .6rem;
  font-family: Arial, sans-serif;
`;

const Grid = styled.div`
  display: grid;
  gap: 0;
`;

const Card = styled.article`
  background: transparent;
  border-top: 1px solid rgba(23, 63, 53, .28);
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  display: grid;
  grid-template-rows: auto 1fr;
  position: relative;
  animation: ${rise} 0.5s ease both;
  animation-delay: ${(props) => `${props.$delay || 0}s`};
  padding: 2rem 0;
  transition: padding-left 0.3s ease;

  &:hover {
    transform: none;
  }

  @media (min-width: 920px) {
    grid-template-columns: minmax(320px, .8fr) minmax(0, 1.2fr);
    grid-template-rows: 1fr;
    gap: clamp(3rem, 7vw, 8rem);
    padding: 4rem 0;
  }
`;

const ImageWrap = styled.div`
  position: relative;
  height: auto;
  aspect-ratio: 4 / 5;
  overflow: hidden;

  @media (min-width: 920px) {
    height: min(60vw, 580px);
    aspect-ratio: auto;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Glow = styled.div`
  display: none;
`;

const CardBadge = styled.span`
  position: absolute;
  top: 0.8rem;
  left: 0.8rem;
  border-radius: 0;
  padding: 0.33rem 0.7rem;
  background: #d9e7a8;
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: #173f35;
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CardBody = styled.div`
  padding: 1.5rem 0 0;
  display: grid;
  gap: 0.85rem;
  align-content: center;
  @media (min-width: 920px) { padding: 0; }
`;

const CardTitle = styled.h3`
  color: #204a3f;
  font-size: clamp(2.5rem, 6vw, 5.5rem);
  font-weight: 450;
  line-height: .9;
  letter-spacing: -.05em;
`;

const CardPitch = styled.p`
  color: #5f5750;
  max-width: 37rem;
  font-family: Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.65;
`;

const Meta = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.8rem;
  color: #345f53;
  border: 0;
  border-bottom: 1px solid rgba(45, 91, 79, 0.2);
  border-radius: 0;
  padding: 0.3rem 0;
  background: transparent;
  font-family: Arial, sans-serif;
`;

const BulletList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.48rem;
`;

const Bullet = styled.li`
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 0.48rem;
  color: #5f5750;
  font-size: 0.96rem;
  line-height: 1.4;
  font-family: Arial, sans-serif;

  svg {
    margin-top: 0.15rem;
    color: #d67646;
  }
`;

const PriceRow = styled.div`
  margin-top: 0.2rem;
  padding-top: 0.65rem;
  border-top: 1px solid rgba(45, 91, 79, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.8rem;
`;

const Price = styled.p`
  color: #1f453b;
  font-size: 1.45rem;
  font-weight: 800;
  margin: 0;
`;

const PriceHint = styled.span`
  color: #7a6d61;
  font-size: 0.8rem;
`;

const CTAButton = styled(NavLink)`
  margin-top: 0.25rem;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  padding: 0.85rem 0.9rem;
  color: #173f35;
  background: #d9e7a8;
  border-radius: 0;
  font-size: 1.04rem;
  font-family: Arial, sans-serif;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-decoration: none;

  &:hover {
    color: #173f35;
    background: #fff;
  }
`;

const CTAArrow = styled(ArrowUpRight)`
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
`;

const FooterCta = styled.div`
  margin-top: 1.8rem;
  text-align: center;
`;

const EventosLink = styled(NavLink)`
  color: #224e43;
  font-weight: 700;
  font-size: clamp(1.02rem, 4.2vw, 1.24rem);
  text-decoration: none;
  border-bottom: 1px solid rgba(34, 78, 67, 0.25);
  padding-bottom: 2px;

  &:hover {
    color: #d67646;
    border-bottom-color: #d67646;
  }
`;

const BASE_EXPERIENCES = [
  {
    id: "pinta",
    image: pintura,
    paymentLink: "https://buy.stripe.com/3cI7sK7glejZcEv59K0RG00",
    slug: "pinta-tu-ceramica-granollers",
  },
  {
    id: "clases",
    image: vino,
    paymentLink: "https://buy.stripe.com/28EbJ00RX0t933V31C0RG03",
    slug: "taller-ceramica-clases-granollers",
  },
  {
    id: "vino",
    image: vino1,
    paymentLink: "https://buy.stripe.com/cNi8wO1W1cbRcEv8lW0RG04",
    slug: "ceramica-y-vino-granollers",
    minPersonas: 4,
  },
];

const copyByLang = {
  es: {
    seoTitle: "Experiencias de cerámica en Vilanova del Vallès | Taller creativo cerca de Granollers",
    seoDescription:
      "Reserva experiencias de cerámica en Vilanova del Vallès, muy cerca de Granollers, Mollet y La Roca del Vallès. Planes creativos para grupos, parejas y familias.",
    ogLocale: "es_ES",
    eyebrow: "Elige tu plan",
    title: "Elige tu experiencia",
    subtitle:
      "Arte, calma y desconexión en un solo lugar. Elige la experiencia que mejor encaje contigo y reserva en minutos.",
    perks: ["Reserva rápida", "Plazas limitadas", "Sin experiencia previa"],
    cta: "Reservar ahora",
    footer: "¿Estás planeando un evento? Descubre experiencias para grupos",
    experiences: {
      pinta: {
        badge: "Top vendido",
        title: "Pinta tu propia cerámica",
        pitch:
          "Una experiencia creativa para desconectar, compartir y llevarte una pieza única hecha por ti.",
        price: "37€",
        priceHint: "por persona",
        meta: ["Guiado", "Flexible", "Coffee Corner Gratis"],
        bullets: [
          "Elige tu pieza y pinta a tu ritmo en un ambiente relajado.",
          "Ideal para amigas, parejas y planes con familia.",
          "Consulta fechas y horarios en el siguiente paso.",
          "Coffee corner con infusión, café y agua saborizada.",
        ],
        reserva: {
          tipo: "Pinta tu cerámica",
          precio: "37€ por persona",
          descripcion: "Clase guiada donde os acompañamos en el proceso de decoración de una pieza cerámica.",
        },
      },
      clases: {
        badge: "Programa mensual",
        title: "Clases de cerámica",
        pitch:
          "Para quienes desean evolucionar en técnica y desarrollar proyectos personales con acompañamiento experto.",
        price: "95€",
        priceHint: "al mes",
        meta: ["2h/semana", "Continuidad", "Coffee Corner Gratis"],
        bullets: [
          "Aprendes técnica y desarrollas tus propias piezas.",
          "No necesitas experiencia previa.",
          "Horarios a convenir según plazas y ritmo del grupo.",
          "Coffee corner incluido en cada clase.",
        ],
        reserva: {
          tipo: "Clases de cerámica",
          precio: "95€ / mes",
          descripcion: "Clases regulares para aprender y desarrollar proyectos.",
        },
      },
      vino: {
        badge: "Ideal amigas",
        title: "Cerámica + vino y picoteo",
        pitch:
          "Una experiencia social y cuidada para grupos que buscan arte, conversación y un ambiente especial.",
        price: "47€",
        priceHint: "por persona",
        meta: ["Min. 4", "Experiencia social", "Coffee Corner Gratis"],
        bullets: [
          "Pintas cerámica mientras disfrutas vino y picoteo.",
          "Perfecto para celebrar o hacer algo distinto.",
          "Sin experiencia previa: solo ganas de disfrutar.",
          "Coffee corner y atención personalizada.",
        ],
        reserva: {
          tipo: "Workshop Pinta Cerámica + Vino",
          precio: "47€ por persona",
          descripcion: "Actividad grupal con cerámica, vino y picoteo.",
        },
      },
    },
  },
  ca: {
    seoTitle: "Experiències de ceràmica a Vilanova del Vallès | Taller creatiu prop de Granollers",
    seoDescription:
      "Reserva experiències de ceràmica a Vilanova del Vallès, molt a prop de Granollers, Mollet i la Roca del Vallès. Plans creatius per a grups, parelles i famílies.",
    ogLocale: "ca_ES",
    eyebrow: "Tria el teu pla",
    title: "Tria la teva experiència",
    subtitle:
      "Art, calma i desconnexió en un sol lloc. Tria l'experiència que millor encaixi amb tu i reserva en minuts.",
    perks: ["Reserva ràpida", "Places limitades", "Sense experiència prèvia"],
    cta: "Reserva ara",
    footer: "Estàs planejant un esdeveniment? Descobreix experiències per a grups",
    experiences: {
      pinta: {
        badge: "Top vendes",
        title: "Pinta la teva pròpia ceràmica",
        pitch:
          "Una experiència creativa per desconnectar, compartir i endur-te una peça única feta per tu.",
        price: "37€",
        priceHint: "per persona",
        meta: ["Guiat", "Flexible", "Coffee Corner Gratis"],
        bullets: [
          "Tria la teva peça i pinta al teu ritme en un ambient relaxat.",
          "Ideal per a amigues, parelles i plans en família.",
          "Consulta dates i horaris al pas següent.",
          "Coffee corner amb infusió, cafè i aigua saboritzada.",
        ],
        reserva: {
          tipo: "Pinta la teva ceràmica",
          precio: "37€ per persona",
          descripcion: "Classe guiada on us acompanyem en el procés de decoració d'una peça ceràmica.",
        },
      },
      clases: {
        badge: "Programa mensual",
        title: "Classes de ceràmica",
        pitch:
          "Per a qui vulgui evolucionar en tècnica i desenvolupar projectes personals amb acompanyament expert.",
        price: "95€",
        priceHint: "al mes",
        meta: ["2h/setmana", "Continuïtat", "Coffee Corner Gratis"],
        bullets: [
          "Aprens tècnica i desenvolupes les teves pròpies peces.",
          "No necessites experiència prèvia.",
          "Horaris a convenir segons places i ritme del grup.",
          "Coffee corner inclòs a cada classe.",
        ],
        reserva: {
          tipo: "Classes de ceràmica",
          precio: "95€ / mes",
          descripcion: "Classes regulars per aprendre i desenvolupar projectes.",
        },
      },
      vino: {
        badge: "Ideal amigues",
        title: "Ceràmica + vi i pica-pica",
        pitch:
          "Una experiència social i cuidada per a grups que busquen art, conversa i un ambient especial.",
        price: "47€",
        priceHint: "per persona",
        meta: ["Mín. 4", "Experiència social", "Coffee Corner Gratis"],
        bullets: [
          "Pintes ceràmica mentre gaudeixes de vi i pica-pica.",
          "Perfecte per celebrar o fer alguna cosa diferent.",
          "Sense experiència prèvia: només ganes de gaudir.",
          "Coffee corner i atenció personalitzada.",
        ],
        reserva: {
          tipo: "Workshop Pinta Ceràmica + Vi",
          precio: "47€ per persona",
          descripcion: "Activitat grupal amb ceràmica, vi i pica-pica.",
        },
      },
    },
  },
};

const Experiencias = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("ca") ? "ca" : "es";
  const copy = copyByLang[lang];

  const experiences = BASE_EXPERIENCES.map((item) => ({
    ...item,
    ...copy.experiences[item.id],
    data: {
      tipo: copy.experiences[item.id].reserva.tipo,
      precio: copy.experiences[item.id].reserva.precio,
      descripcion: copy.experiences[item.id].reserva.descripcion,
      slug: item.slug,
      paymentLink: item.paymentLink,
      minPersonas: item.minPersonas || 1,
    },
  }));

  const guardarExperiencia = (data) => {
    localStorage.setItem("experienciaSeleccionada", JSON.stringify(data));
  };

  return (
    <>
      <Helmet>
        <title>{copy.seoTitle}</title>
        <meta name="description" content={copy.seoDescription} />
        <meta property="og:title" content={copy.seoTitle} />
        <meta property="og:description" content={copy.seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={copy.ogLocale} />
      </Helmet>

      <Wrapper>
        <Container>
          <Hero>
            <Eyebrow>
              <FaStar />
              {copy.eyebrow}
            </Eyebrow>
            <Title>{copy.title}</Title>
            <Subtitle>{copy.subtitle}</Subtitle>
            <Perks>
              {copy.perks.map((perk) => (
                <Perk key={perk}>{perk}</Perk>
              ))}
            </Perks>
          </Hero>

          <Grid>
            {experiences.map((experience, index) => (
              <Card key={experience.id} $delay={0.08 * index}>
                <ImageWrap>
                  <CardImage src={experience.image} alt={experience.title} loading="lazy" />
                  <Glow />
                  <CardBadge>{experience.badge}</CardBadge>
                </ImageWrap>

                <CardBody>
                  <CardTitle>{experience.title}</CardTitle>
                  <CardPitch>{experience.pitch}</CardPitch>

                  <Meta>
                    {experience.meta.map((metaItem, idx) => (
                      <MetaItem key={`${experience.id}-meta-${idx}`}>
                        {idx === 0 ? <FaClock /> : <FaUsers />}
                        {metaItem}
                      </MetaItem>
                    ))}
                  </Meta>

                  <BulletList>
                    {experience.bullets.map((item) => (
                      <Bullet key={item}>
                        <FaCheckCircle />
                        <span>{item}</span>
                      </Bullet>
                    ))}
                  </BulletList>

                  <PriceRow>
                    <Price>{experience.price}</Price>
                    <PriceHint>{experience.priceHint}</PriceHint>
                  </PriceRow>

                  <CTAButton to="/reservas" onClick={() => guardarExperiencia(experience.data)}>
                    {copy.cta}
                    <CTAArrow aria-hidden="true" />
                  </CTAButton>
                </CardBody>
              </Card>
            ))}
          </Grid>

          <FooterCta>
            <EventosLink to="/eventos">{copy.footer}</EventosLink>
          </FooterCta>
        </Container>
      </Wrapper>
    </>
  );
};

export default Experiencias;
